import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["priceSection", "priceInput", "priceHidden", "pinSection", "pinInput", "previewGrid", "submitBtn", "fileInput", "dropzoneLabel"]

  connect() {
    this._currentFiles = []
    this.updatePriceVisibility()
  }

  togglePrice() {
    this.updatePriceVisibility()
  }

  updatePriceVisibility() {
    const freeRadio = this.element.querySelector('input[name="listing[price_type]"][value="free"]')
    const isFree = freeRadio && freeRadio.checked

    if (this.hasPriceSectionTarget) {
      this.priceSectionTarget.style.display = isFree ? "none" : "block"
    }

    if (this.hasPriceInputTarget) {
      if (isFree) {
        this.priceInputTarget.value = ""
        this.priceInputTarget.removeAttribute("required")
      } else {
        this.priceInputTarget.setAttribute("required", "required")
      }
    }

    if (this.hasPinSectionTarget) {
      this.pinSectionTarget.style.display = "block"
    }
    if (this.hasPinInputTarget) {
      this.pinInputTarget.setAttribute("required", "required")
    }

    this.element.querySelectorAll('input[name="listing[price_type]"]').forEach(radio => {
      const outerSpan = radio.nextElementSibling
      if (!outerSpan) return
      const dot = outerSpan.querySelector("span")
      if (!dot) return
      if (radio.checked) {
        dot.style.opacity = "1"
        outerSpan.style.borderColor = "var(--neon-primary)"
        outerSpan.style.boxShadow = "0 0 8px var(--neon-primary-30)"
      } else {
        dot.style.opacity = "0"
        outerSpan.style.borderColor = "var(--neon-primary-30)"
        outerSpan.style.boxShadow = "none"
      }
    })
  }

  previewImages(event) {
    const newFiles = Array.from(event.target.files)
    if (newFiles.length === 0) return

    const MAX_FILES = 5
    const available = MAX_FILES - this._currentFiles.length

    if (available <= 0) {
      alert(`이미 최대 ${MAX_FILES}장이 선택되었습니다.`)
      return
    }

    const toAdd = newFiles.slice(0, available)
    if (newFiles.length > available) {
      alert(`${available}장만 추가됩니다. (최대 ${MAX_FILES}장 제한)`)
    }

    this._currentFiles = [...this._currentFiles, ...toAdd]

    const dt = new DataTransfer()
    this._currentFiles.forEach(f => dt.items.add(f))
    this.fileInputTarget.files = dt.files

    this.renderPreviews()
  }

  renderPreviews() {
    if (!this.hasPreviewGridTarget) return

    const MAX_FILES = 5
    const grid = this.previewGridTarget
    grid.innerHTML = ""

    this._currentFiles.forEach((file, index) => {
      const wrapper = document.createElement("div")
      wrapper.style.cssText = "position:relative; overflow:hidden; border-radius:2px; border:1px solid var(--neon-primary-30);"

      if (file.type.startsWith("image/")) {
        const reader = new FileReader()
        reader.onload = (e) => {
          const img = document.createElement("img")
          img.src = e.target.result
          img.alt = `Screenshot ${index + 1}`
          img.style.cssText = "width:100%; height:80px; object-fit:cover; display:block;"

          const badge = document.createElement("span")
          badge.style.cssText = "position:absolute; bottom:3px; left:3px; font-family:'JetBrains Mono',monospace; font-size:9px; color:var(--neon-primary-60); background:rgba(0,0,0,0.75); padding:1px 4px; border-radius:1px; pointer-events:none;"
          badge.textContent = `${index + 1}/${MAX_FILES}`

          const removeBtn = document.createElement("button")
          removeBtn.type = "button"
          removeBtn.style.cssText = "position:absolute; top:4px; right:4px; width:18px; height:18px; background:var(--semantic-danger-50); color:#fff; border-radius:50%; font-size:12px; font-weight:bold; display:none; align-items:center; justify-content:center; border:none; cursor:pointer; z-index:10; padding:0; line-height:1;"
          removeBtn.textContent = "×"
          removeBtn.dataset.action = "click->listing-form#removeImage"
          removeBtn.dataset.index = index

          wrapper.addEventListener("mouseenter", () => { removeBtn.style.display = "flex" })
          wrapper.addEventListener("mouseleave", () => { removeBtn.style.display = "none" })

          wrapper.appendChild(img)
          wrapper.appendChild(badge)
          wrapper.appendChild(removeBtn)
        }
        reader.readAsDataURL(file)
      }

      grid.appendChild(wrapper)
    })

    if (this._currentFiles.length > 0 && this._currentFiles.length < MAX_FILES) {
      const remaining = MAX_FILES - this._currentFiles.length
      const addSlot = document.createElement("div")
      addSlot.style.cssText = "height:80px; border:1px dashed var(--neon-primary-30); border-radius:2px; display:flex; flex-direction:column; align-items:center; justify-content:center; cursor:pointer; background:var(--neon-primary-04); gap:4px; transition:border-color 0.2s, background 0.2s;"
      addSlot.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--neon-primary-60)" stroke-width="2.5" stroke-linecap="round">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        <span style="font-family:'JetBrains Mono',monospace; font-size:9px; color:var(--neon-primary-60); text-align:center; line-height:1.5;">+${remaining}장<br>추가</span>
      `
      addSlot.addEventListener("mouseover", () => {
        addSlot.style.borderColor = "var(--neon-primary-60)"
        addSlot.style.background = "var(--neon-primary-04)"
      })
      addSlot.addEventListener("mouseout", () => {
        addSlot.style.borderColor = "var(--neon-primary-30)"
        addSlot.style.background = "var(--neon-primary-04)"
      })
      addSlot.addEventListener("click", () => this.fileInputTarget.click())
      grid.appendChild(addSlot)
    }

    if (this.hasDropzoneLabelTarget) {
      this.dropzoneLabelTarget.style.display = this._currentFiles.length > 0 ? "none" : ""
    }
  }

  removeImage(event) {
    event.stopPropagation()
    const index = parseInt(event.currentTarget.dataset.index)
    this._currentFiles = this._currentFiles.filter((_, i) => i !== index)

    const dt = new DataTransfer()
    this._currentFiles.forEach(f => dt.items.add(f))
    this.fileInputTarget.files = dt.files

    this.renderPreviews()
  }

  // ── handleSubmit(event) ───────────────────────────────────
  // 폼 submit 이벤트에 연결 (data-action="submit->listing-form#handleSubmit")
  // - 제출 버튼 비활성화 (중복 제출 방지)
  // - 블록체인 테마 로딩 오버레이 표시
  // - 폼 제출은 기본 동작으로 계속 진행 (preventDefault 없음)
  handleSubmit(event) {
    // 제출 버튼 비활성화하여 중복 클릭 방지
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
    }

    // 블록체인 로딩 오버레이 표시
    this._showLoadingOverlay()
  }

  // ── Private: _showLoadingOverlay() ───────────────────────
  // 블록체인 테마 전체 화면 로딩 오버레이를 body에 삽입
  // 디자인: 네온 그린(var(--neon-primary)), JetBrains Mono, 회전 링 + 내부 텍스트 순환
  _showLoadingOverlay() {
    // 기존 오버레이가 있으면 제거 (중복 방지)
    const existing = document.getElementById("blockchain-loading-overlay")
    if (existing) existing.remove()

    // ── CSS 키프레임 및 글로벌 스타일 (한 번만 삽입) ──
    if (!document.getElementById("bc-loading-styles")) {
      const style = document.createElement("style")
      style.id = "bc-loading-styles"
      style.textContent = `
        @keyframes bcOverlayFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes bcSpinOuter {
          to { transform: rotate(360deg); }
        }
        @keyframes bcSpinInner {
          to { transform: rotate(360deg); }
        }
        @keyframes bcPulse {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50%       { opacity: 1; transform: scale(1.08);
                      filter: drop-shadow(0 0 6px var(--neon-primary-60)); }
        }
        @keyframes bcDotBlink {
          0%, 80%, 100% { background: var(--neon-primary-30); transform: scale(0.9); }
          40%           { background: var(--neon-primary-60); transform: scale(1.3);
                          box-shadow: 0 0 6px var(--neon-primary-60); }
        }
      `
      document.head.appendChild(style)
    }

    // ── 오버레이 컨테이너 ──
    // 전체 화면을 덮는 반투명 다크 배경
    const overlay = document.createElement("div")
    overlay.id = "blockchain-loading-overlay"
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: rgba(10, 10, 10, 0.92);
      backdrop-filter: blur(4px);
      font-family: 'JetBrains Mono', monospace;
      animation: bcOverlayFadeIn 0.25s ease-out;
    `

    // ── 스피너 래퍼 ──
    // 바깥 회전 링 + 안쪽 역방향 링 + 중앙 블록체인 아이콘
    const spinnerWrap = document.createElement("div")
    spinnerWrap.style.cssText = `
      position: relative;
      width: 120px;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
    `

    // 바깥쪽 회전 링 (시계 방향, 네온 그린)
    const ringOuter = document.createElement("div")
    ringOuter.style.cssText = `
      position: absolute;
      inset: 0;
      border-radius: 50%;
      border: 2px solid transparent;
      border-top-color: var(--neon-primary);
      border-right-color: var(--neon-primary-30);
      animation: bcSpinOuter 1.2s linear infinite;
      box-shadow: 0 0 14px var(--neon-primary-30);
    `

    // 안쪽 회전 링 (반시계 방향, 더 빠르게)
    const ringInner = document.createElement("div")
    ringInner.style.cssText = `
      position: absolute;
      inset: 18px;
      border-radius: 50%;
      border: 1.5px solid transparent;
      border-bottom-color: var(--neon-primary-60);
      border-left-color: var(--neon-primary-30);
      animation: bcSpinInner 0.7s linear infinite reverse;
    `

    // 중앙 아이콘 (체인/블록체인 느낌)
    const centerIcon = document.createElement("div")
    centerIcon.style.cssText = `
      width: 38px;
      height: 38px;
      background: var(--neon-primary-08);
      border: 1px solid var(--neon-primary-30);
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      animation: bcPulse 1.5s ease-in-out infinite;
    `
    centerIcon.textContent = "⛓"

    spinnerWrap.appendChild(ringOuter)
    spinnerWrap.appendChild(ringInner)
    spinnerWrap.appendChild(centerIcon)

    // ── 주 상태 텍스트 ──
    const statusText = document.createElement("div")
    statusText.id = "bc-status-text"
    statusText.style.cssText = `
      margin-top: 30px;
      font-size: 13px;
      font-weight: 700;
      color: var(--neon-primary);
      letter-spacing: 0.12em;
      text-transform: uppercase;
      text-shadow: 0 0 10px var(--neon-primary-60);
      transition: opacity 0.3s ease;
    `
    statusText.textContent = "INITIATING TRANSACTION..."

    // ── 서브 상태 텍스트 (순환 메시지) ──
    const subText = document.createElement("div")
    subText.id = "bc-sub-text"
    subText.style.cssText = `
      margin-top: 8px;
      font-size: 10px;
      color: var(--neon-primary-60);
      letter-spacing: 0.1em;
      text-transform: uppercase;
    `
    subText.textContent = "AWAITING CONFIRMATION"

    // ── 하단 점(dot) 3개 순차 점멸 인디케이터 ──
    const dotsWrap = document.createElement("div")
    dotsWrap.style.cssText = `
      display: flex;
      gap: 7px;
      margin-top: 24px;
    `
    for (let i = 0; i < 3; i++) {
      const dot = document.createElement("span")
      dot.style.cssText = `
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--neon-primary-30);
        animation: bcDotBlink 1.2s ease-in-out infinite;
        animation-delay: ${i * 0.2}s;
        display: inline-block;
      `
      dotsWrap.appendChild(dot)
    }

    // ── 오버레이에 요소 삽입 ──
    overlay.appendChild(spinnerWrap)
    overlay.appendChild(statusText)
    overlay.appendChild(subText)
    overlay.appendChild(dotsWrap)
    document.body.appendChild(overlay)

    // ── 상태 텍스트 순환 애니메이션 ──
    // 약 1.8초 간격으로 블록체인 처리 단계 메시지를 순환
    const messages = [
      { main: "INITIATING TRANSACTION...", sub: "AWAITING CONFIRMATION" },
      { main: "BROADCASTING TX...",        sub: "PROPAGATING TO NODES" },
      { main: "VALIDATING BLOCK...",       sub: "CONSENSUS IN PROGRESS" },
      { main: "RECORDING ON-CHAIN...",     sub: "IMMUTABLE LEDGER UPDATE" },
      { main: "FINALIZING...",             sub: "ALMOST THERE" },
    ]
    let msgIndex = 0
    const msgInterval = setInterval(() => {
      msgIndex = (msgIndex + 1) % messages.length
      const el = document.getElementById("bc-status-text")
      const subEl = document.getElementById("bc-sub-text")
      if (!el) {
        // 오버레이가 사라진 경우 인터벌 정리
        clearInterval(msgInterval)
        return
      }
      // 페이드 아웃 → 텍스트 교체 → 페이드 인
      el.style.opacity = "0"
      setTimeout(() => {
        if (el) el.textContent = messages[msgIndex].main
        if (subEl) subEl.textContent = messages[msgIndex].sub
        if (el) el.style.opacity = "1"
      }, 300)
    }, 1800)
  }
}
